import { useRef, useState } from "react"
import { PersonAvatar } from "@/components/people/person-avatar"
import { UserMenu } from "@/components/shell/user-menu"
import { CollapsedNavPeekProvider, SidebarTooltip } from "@/components/ui/sidebar-tooltip"
import { useTranslation } from "@/hooks/use-translation"
import { personDisplayName } from "@/lib/person-name"
import { cn } from "@/lib/utils"
import type { AuthUser } from "@/types/auth"

export type SidebarUserFooterProps = {
  user: AuthUser | null
  collapsed: boolean
  active: boolean
  railHovered: boolean
  onRailHoverChange: (hovered: boolean) => void
  onAction: () => void
}

export function SidebarUserFooter({
  user,
  collapsed,
  active,
  railHovered,
  onRailHoverChange,
  onAction,
}: SidebarUserFooterProps) {
  const { t } = useTranslation("layout")
  const [menuOpen, setMenuOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const highlighted = active || menuOpen
  const displayName = user ? personDisplayName(user) : ""
  const hasName = user ? displayName !== user.email : false
  const toggleMenu = () => setMenuOpen((value) => !value)

  const menu = user ? (
    <UserMenu open={menuOpen} onOpenChange={setMenuOpen} anchorRef={triggerRef} rootRef={rootRef} onAction={onAction} />
  ) : null

  if (collapsed) {
    return (
      <div ref={rootRef} className="flex h-11 items-center justify-center">
        {user ? (
          <div onMouseEnter={() => onRailHoverChange(true)} onMouseLeave={() => onRailHoverChange(false)}>
            <CollapsedNavPeekProvider enabled={!menuOpen} railHovered={railHovered}>
              <SidebarTooltip label={hasName ? `${displayName} · ${user.email}` : user.email}>
                <button
                  ref={triggerRef}
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-label={t("shell.userMenu.open")}
                  onClick={toggleMenu}
                  className={cn(
                    "rounded-lg focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none",
                    highlighted && "ring-2 ring-[var(--ui-nav-active-bg)] ring-offset-2 ring-offset-ui-bg-elevated",
                  )}
                >
                  <PersonAvatar person={user} size={32} />
                </button>
              </SidebarTooltip>
            </CollapsedNavPeekProvider>
          </div>
        ) : null}
        {menu}
      </div>
    )
  }

  return (
    <div ref={rootRef} className="flex items-center gap-1">
      {user ? (
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={t("shell.userMenu.open")}
          onClick={toggleMenu}
          className={cn(
            "flex h-9 min-w-0 flex-1 items-center gap-2.5 rounded-lg px-1.5 text-left transition-colors",
            "focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none",
            highlighted
              ? "bg-[var(--ui-nav-active-bg)] light:shadow-[var(--shadow-clay-inset)]"
              : "hover:bg-[var(--ui-nav-idle-hover-bg)]",
          )}
        >
          <PersonAvatar person={user} size={32} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] leading-[18px] font-medium text-ui-text-primary">
              {displayName}
            </span>
            {hasName ? (
              <span className="block truncate text-[11px] leading-4 text-ui-text-muted">{user.email}</span>
            ) : null}
          </span>
        </button>
      ) : (
        <div className="flex-1" />
      )}
      {menu}
    </div>
  )
}
